import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const PythonSyntax = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Programming Fundamentals</div>
                <h1>Python Syntax & Code Structure</h1>
                <p className="hero-lead">
                    Master the elegance of Python's design philosophy. From significant whitespace to the Zen of Python,
                    learn how strict syntax rules create some of the most readable and maintainable code in the world.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Python is often described as "executable pseudocode". This isn't an accident; it's a deliberate design
                    choice rooted in the language's philosophy. Unlike C-family languages (C++, Java, JavaScript) where formatting
                    is optional and structure is defined by punctuation (curly braces <code>{`{}`}</code> and semicolons <code>;</code>),
                    Python elevates formatting to a syntactic requirement.
                </p>
                <p>
                    This unique approach, known as <strong>Significant Whitespace</strong>, eliminates the debate over "bracket placement"
                    and forces all developers to adhere to a consistent visual structure. The result is a codebase that looks remarkably
                    similar regardless of who wrote it, significantly reducing the cognitive load required to read and review code.
                </p>
                <p>
                    In this comprehensive guide, we will go beyond the basics. We will explore how the Python parser interprets indentation,
                    the fundamental difference between statements and expressions (and why that distinction matters for functional programming features),
                    and the industry-standard style guides that separate amateur scripts from professional software engineering.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Core Philosophy:</strong> The "Zen of Python" and how it guides syntax decisions.</li>
                        <li><strong>Significant Whitespace:</strong> How Python's parser uses INDENT and DEDENT tokens.</li>
                        <li><strong>Execution Model:</strong> The strict distinction between Statements and Expressions.</li>
                        <li><strong>Documentation:</strong> Writing professional Docstrings that power IDE autocompletion.</li>
                        <li><strong>PEP 8 Standards:</strong> The official style guide for the Python ecosystem.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Zen of Python: Guiding Principles</h2>
                <p>
                    Before diving into rules, it's crucial to understand the <em>mindset</em> of Python. Tim Peters wrote a collection
                    of 19 aphorisms known as "The Zen of Python" that summarize the language's design philosophy. You can view these
                    anytime by running <code>import this</code> in a Python shell.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Zen of Python (Selected)</span>
                    </div>
                    <CodeBlock language="python" code={`import this

# Selected aphorisms that directly influence syntax:

# 1. "Readability counts."
#    - Explains why we use words like 'and', 'or', 'not' instead of symbols like &&, ||, !
#    - Explains significant whitespace to enforce visual clarity.

# 2. "Explicit is better than implicit."
#    - Explains why there's no implicit type coercion (e.g., "5" + 5 raises an error).

# 3. "There should be one-- and preferably only one --obvious way to do it."
#    - Unlike Perl ("There's More Than One Way To Do It"), Python strives for a single standard.

# 4. "Flat is better than nested."
#    - Encourages early returns and guard clauses to avoid deep indentation levels.`} />
                </div>

                <h3>Detailed Analysis</h3>
                <p>
                    <strong>Why this matters:</strong> Understanding these principles helps you predict how Python behaves.
                    For instance, "Readability counts" is why Python uses indentation instead of braces. Braces add visual noise;
                    indentation provides structure without the clutter. "Explicit is better than implicit" is why Python requires
                    <code>self</code> in method definitionsit's explicit about the instance being operated on, whereas other languages
                    hide it.
                </p>
            </div>

            <div className="content-section">
                <h2>A Note on History: Python 2 vs 3</h2>
                <p>
                    Python 3 (released in 2008) introduced breaking syntax changes to fix long-standing design flaws.
                    Although Python 2 is end-of-life, you might encounter legacy code in the wild.
                </p>
                <p>
                    <strong>Key Syntax Differences:</strong>
                </p>
                <ul>
                    <li><strong>Print:</strong> Python 2 made `print` a statement (<code>print "Hello"</code>).
                        Python 3 made it a function (<code>print("Hello")</code>). This follows the "Explicit is better" rulewhy should printing be a special keyword?</li>
                    <li><strong>Integer Division:</strong> in Python 2, <code>3 / 2</code> was <code>1</code>. In Python 3, it is <code>1.5</code>.
                        If you want floor division, use <code>//</code> explicitly.</li>
                    <li><strong>Unicode:</strong> Python 3 strings are Unicode by default. Python 2 strings were bytes, causing endless encoding headaches.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Significant Whitespace: Under the Hood</h2>
                <p>
                    In most languages, whitespace (spaces, tabs, newlines) is ignored by the compiler. You could write an entire
                    Java program on a single line. In Python, whitespace is a syntactic element. This is often the most jarring
                    change for new Pythonistas, but it quickly becomes a beloved feature.
                </p>
                <p>
                    <strong>How it works internally:</strong> When the Python lexer reads your source code, it doesn't just see spaces.
                    It generates special tokens: <code>INDENT</code> (when indentation increases) and <code>DEDENT</code> (when it decreases).
                    These tokens play the exact same role as <code>{`{`}</code> and <code>{`}`}</code> in other languages.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Structure via Indentation</span>
                    </div>
                    <CodeBlock language="python" code={`def calculate_status(score):
    # INDENT token generated here (Level 1)
    print("Analyzing score...")
    
    if score >= 90:
        # INDENT token generated here (Level 2)
        status = "Excellent"
        notify_user()
    # DEDENT token generated (back to Level 1)
    
    elif score >= 50:
        # INDENT Level 2
        status = "Passed"
    # DEDENT Level 1
    
    else:
        # INDENT Level 2
        status = "Failed"
    # DEDENT Level 1

    return status
# DEDENT Level 0 (End of function)`} />
                </div>

                <h3>Code Walkthrough</h3>
                <p>
                    Let's break down the mechanics of the block structure above:
                </p>
                <ul>
                    <li><strong>Line 1:</strong> The colon <code>:</code> indicates that a new block is starting. The next line <em>must</em> be indented.</li>
                    <li><strong>Line 2-3:</strong> These lines share the same indentation level (4 spaces). They belong to the same block (the function body).</li>
                    <li><strong>Line 5:</strong> Another colon introduces a nested block for the <code>if</code> statement.</li>
                    <li><strong>Line 6-7:</strong> These are indented further (8 spaces total). They execute only if the condition is true.</li>
                    <li><strong>Line 17:</strong> This line aligns with the <code>if</code> and <code>print</code> statements, meaning it is part of the function body but <em>outside</em> the if/else logic. It runs regardless of the condition.</li>
                </ul>

                <Warning><strong> Critical Rule: Mixing Tabs and Spaces</strong>
                    <p>
                        Never mix tabs and spaces. Python 3 makes this a syntax error (`TabError`).
                        The standard is strictly <strong>4 spaces</strong> per indentation level.
                        Configure your editor (VS Code, PyCharm) to "Insert Spaces" when you press the Tab key.
                    </p></Warning>
            </div>

            <div className="content-section">
                <h2>Statements vs Expressions: A Crucial Distinction</h2>
                <p>
                    In Python, the distinction between a <strong>Statement</strong> and an <strong>Expression</strong> is strict and important,
                    especially as you move into advanced functional programming or use features like <code>lambda</code>.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Feature</th>
                                <th>Expression</th>
                                <th>Statement</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Definition</strong></td>
                                <td>Code that evaluates to a value (Object).</td>
                                <td>Code that performs an action or controls flow.</td>
                            </tr>
                            <tr>
                                <td><strong>Return Value</strong></td>
                                <td>Always returns something (even <code>None</code>).</td>
                                <td>Does not return a value itself.</td>
                            </tr>
                            <tr>
                                <td><strong>Examples</strong></td>
                                <td><code>3 + 5</code>, <code>map()</code>, <code>"hello"</code>, <code>x &gt; 5</code></td>
                                <td><code>if</code>, <code>for</code>, <code>def</code>, <code>import</code>, <code>return</code></td>
                            </tr>
                            <tr>
                                <td><strong>Placement</strong></td>
                                <td>Can be used on the right side of <code>=</code>.</td>
                                <td>Cannot be assigned to a variable.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Expressions vs Statements in Action</span>
                    </div>
                    <CodeBlock language="python" code={`# EXPRESSIONS: Everything here evaluates to a value
3 + 4             # Evaluates to 7
"Hello".upper()   # Evaluates to "HELLO"
x > 10            # Evaluates to boolean True/False
my_list[0]        # Evaluates to the item at index 0

# STATEMENTS: These perform structural actions
x = 5             # Assignment Statement
if x > 0:         # If Statement (Control Flow)
    pass

# The "Walrus Operator" (Python 3.8+) bridges the gap
# It allows assignment (normally a statement) to be an expression!

# without walrus (Statement approach)
value = get_data()
if value:
    print(value)

# with walrus (Expression approach)
if (value := get_data()):  # Assigns AND returns the value
                    print(value)`} />
                </div>

                <h3>Why This Matters</h3>
                <p>
                    This distinction explains limitations you might encounter. For example, a Python <code>lambda</code> (anonymous function)
                    is restricted to a single <strong>expression</strong>. You cannot put an <code>if</code> <em>statement</em> or a <code>for</code> loop inside a lambda,
                    only conditional <em>expressions</em> or list comprehensions.
                </p>
                <p>
                    <strong>Example:</strong> <code>lambda x: if x &gt; 0: return x</code> is invalid Syntax. <br />
                    <strong>Fix:</strong> <code>lambda x: x if x &gt; 0 else 0</code> (using ternary conditional expression).
                </p>
            </div>

            <div className="content-section">
                <h2>Syntactic Sugar: Decorators</h2>
                <p>
                    "Syntactic Sugar" refers to syntax that makes code easier to read or express, even if it doesn't add new functionality to the language.
                    The most famous example in Python is the <strong>Decorator</strong> syntax (<code>@</code>).
                </p>
                <p>
                    Decorators are simply functions that wrap other functions. Before Python 2.4, you had to manually reassign the function.
                    The <code>@Symbol</code> syntax introduced a cleaner way to apply these wrappers at definition time.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The @Syntax vs Manual Assignment</span>
                    </div>
                    <CodeBlock language="python" code={`# The "Sugared" Syntax (What we use today)
@login_required
def view_profile(user):
    return user.profile

# What Python actually executes (The Desugared version)
def view_profile(user):
    return user.profile
    
view_profile = login_required(view_profile)  # Manual reassignment`} />
                </div>

                <h3>Code Walkthrough</h3>
                <p>
                    When the Python parser sees the <code>@login_required</code> line, it knows that the very next function definition
                    should be passed as an argument to <code>login_required</code>. The return value of that call then replaces the original function name.
                    This elegant syntax encourages the "Composition over Inheritance" design pattern.
                </p>
            </div>

            <div className="content-section">
                <h2>Modern Syntax: Type Hints</h2>
                <p>
                    Python 3.5 introduced a major syntax addition: <strong>Type Hints</strong>. While Python remains a dynamically typed language
                    (types are checked at runtime), this syntax allows you to annotate your code with expected types.
                    These annotations are ignored by the Python interpreter during execution but are gold for static analysis tools like <code>mypy</code> and IDEs.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Type Hinting Syntax</span>
                    </div>
                    <CodeBlock language="python" code={`from typing import List, Optional

# Variable Annotation
age: int = 25
name: str = "Alice"

# Function Annotation
def search_users(query: str, limit: int = 10) -> List[str]:
    """Search for users matching the query."""
    return ["user1", "user2"]

# Optional types (can be None)
def get_bio(user_id: int) -> Optional[str]:
    if user_id == 0:
        return None
    return "This is a bio"`} />
                </div>

                <h3>Understanding the Syntax</h3>
                <ul>
                    <li><strong>Colon <code>:</code></strong> Separates variable name from type.</li>
                    <li><strong>Arrow <code>-&gt;</code></strong> Indicates the return type of a function.</li>
                    <li><strong>Runtime Behavior:</strong> These are 100% ignored at runtime! You can pass a string to an <code>int</code> annotated function
                        and Python will run it without error. The syntax exists solely for developer tooling and documentation.</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Comments and Documentation: The Professional Way</h2>
                <p>
                    Writing code is easy; writing code that others (and your future self) can understand is the challenge.
                    Python offers two distinct tools for this: <strong>Comments</strong> for implementation details, and
                    <strong>Docstrings</strong> for interface specifications.
                </p>

                <h3>1. Comments (#)</h3>
                <p>
                    Comments are for the developer maintaining the code. They explain <em>why</em> a specific logic was chosen,
                    typically when the code itself isn't obvious.
                </p>
                <ul>
                    <li>Start with a hash character <code>#</code>.</li>
                    <li>Should be complete sentences.</li>
                    <li><strong>Avoid obvious comments:</strong> <code>x = x + 1 # Increment x</code> is noise. Remove it.</li>
                </ul>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Pro Tip: Tracking Technical Debt</strong><br />
                        Use <code># TODO:</code> or <code># FIXME:</code> comments to mark areas that need future work.
                        Most modern IDEs (like VS Code and PyCharm) automatically index these tags, creating a searchable
                        list of tasks directly within your codebase.
                    </div></ProTip>

                <h3>2. Docstrings (""")</h3>
                <p>
                    Docstrings (Documentation Strings) are a unique Python feature. They are the first statement in a module,
                    function, or class, enclosed in triple quotes. Unlike comments, <strong>Docstrings are retained at runtime</strong>.
                </p>
                <p>
                    Python automatically assigns the docstring content to the object's <code>__doc__</code> attribute. This is what tools like
                    <code>help()</code>, Sphinx, and IDEs use to show pop-up documentation.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Google Style Docstrings (Industry Standard)</span>
                    </div>
                    <CodeBlock language="python" code={`def process_payment(amount, currency="USD"):
    """
    Process a user payment through the secure gateway.

    This function handles currency conversion, validation, and API communication.
    It includes retry logic for network timeouts.

    Args:
        amount (float): The transaction amount. Must be positive.
        currency (str, optional): The 3-letter ISO currency code. 
            Defaults to "USD".

    Returns:
        bool: True if payment succeeded, False otherwise.

    Raises:
        ValueError: If amount is negative.
        ConnectionError: If payment gateway is unreachable.
    """
    if amount <= 0:
        raise ValueError("Amount must be positive")
    # ... implementation details ...
    return True

# Accessing documentation programmatically
print(process_payment.__doc__)`} />
                </div>

                <h3>Code Walkthrough</h3>
                <ul>
                    <li><strong>Summary Line:</strong> The first line is a concise summary.</li>
                    <li><strong>Details:</strong> A paragraph explaining behavior and side effects.</li>
                    <li><strong>Args/Returns/Raises:</strong> Structured sections defining inputs and outputs. This allows IDEs to warn you if you pass the wrong types!</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Line Continuation: Implicit vs Explicit</h2>
                <p>
                    PEP 8 recommends limiting lines to 79 characters. When code gets too long, you need to break it.
                    There are two ways to do this, but one is heavily preferred.
                </p>

                <h3>1. Implicit Continuation (Preferred)</h3>
                <p>
                    Python's parser automatically continues reading the next line if it detects an unclosed parenthesis <code>()</code>,
                    bracket <code>[]</code>, or brace <code>{`{}`}</code>. This is the cleanest way to format long lines.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Implicit Continuation Examples</span>
                    </div>
                    <CodeBlock language="python" code={`# Function arguments
user = create_user(
    username="john_doe",
    email="john@example.com",
    age=30,
    is_active=True
)

# Mathematical operations
total_score = (
    base_points
    + bonus_points
    - penalty_points
    * difficulty_multiplier
)

# Lists and Dictionaries
config = {
    "debug": True,
    "database": "postgresql://localhost:5432",
    "retries": 5,
}`} />
                </div>

                <h3>2. Explicit Continuation (Discouraged)</h3>
                <p>
                    You can force a line break using a backslash <code>\</code>. This is considered fragile because adding a space
                    after the backslash will cause a SyntaxError. Use this only when implicit continuation is impossible (e.g., long <code>with</code> statements).
                </p>
                <div className="code-example">
                    <div className="code-header">
                        <span>Explicit Continuation (Use Sparingly)</span>
                    </div>
                    <CodeBlock language="python" code={`#  Fragile: A space after \\ will break the code
if condition_one and \
   condition_two and \
   condition_three:
    do_something()`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Syntax Showdown: Python vs The World</h2>
                <p>
                    If you are coming from C++, Java, or JavaScript, Python's syntax might feel "naked."
                    Here is a direct comparison to help you map your existing knowledge to Python's structure.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Concept</th>
                                <th>C++ / Java / C#</th>
                                <th>Python</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Block Wrapper</strong></td>
                                <td>Curly Braces <code>{`{ ... }`}</code></td>
                                <td>Colon <code>:</code> + Indentation</td>
                            </tr>
                            <tr>
                                <td><strong>Statement End</strong></td>
                                <td>Semicolon <code>;</code></td>
                                <td>Newline</td>
                            </tr>
                            <tr>
                                <td><strong>Variables</strong></td>
                                <td>Typed Declaration <code>int x = 5;</code></td>
                                <td>Dynamic Assignment <code>x = 5</code></td>
                            </tr>
                            <tr>
                                <td><strong>Boolean Logic</strong></td>
                                <td>Symbols <code>&&</code>, <code>||</code>, <code>!</code></td>
                                <td>Words <code>and</code>, <code>or</code>, <code>not</code></td>
                            </tr>
                            <tr>
                                <td><strong>Null Value</strong></td>
                                <td><code>null</code> or <code>nullptr</code></td>
                                <td><code>None</code></td>
                            </tr>
                            <tr>
                                <td><strong>Increment</strong></td>
                                <td><code>x++</code> or <code>++x</code></td>
                                <td><code>x += 1</code> (No <code>++</code> operator)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices & Common Pitfalls</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3> The Pythonic Way</h3>
                        <ul>
                            <li><strong>Follow PEP 8:</strong> Use <code>snake_case</code> for functions/variables and <code>PascalCase</code> for classes.</li>
                            <li><strong>Use Docstrings:</strong> Document <em>everything</em> public. Your team will thank you.</li>
                            <li><strong>Explicit Imports:</strong> Use <code>from module import function</code> or <code>import module</code>. Never use <code>from module import *</code> (wildcard), as it pollutes the namespace and hides dependencies.</li>
                            <li><strong>Flat Structure:</strong> Avoid nesting <code>if</code> statements 5 levels deep. Use guard clauses (`return` early) to keep code flat.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Common Pitfalls</h3>
                        <ul>
                            <li><strong>Mutable Defaults:</strong> Never use a mutable object (list/dict) as a default argument. <br /><code>def bad(a=[]):</code> -&gt; <code>def good(a=None):</code>.</li>
                            <li><strong>Shadowing Built-ins:</strong> Don't name variables <code>list</code>, <code>str</code>, or <code>id</code>. It breaks the built-in functions.</li>
                            <li><strong>Mixing Indentation:</strong> Even if it looks aligned, mixing tabs and spaces will crash Python 3.</li>
                            <li><strong>Reinventing Wheels:</strong> Before writing a complex sorting or searching function, check the standard library. Python likely has it built-in and optimized in C.</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonSyntax;






